/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { ControlledTree, SelectionMode, useTreeModel } from "@itwin/components-react";
import { useTreeEventsHandler, useTreeModelSource, useTreeNodeLoader } from "@itwin/components-react";
import { AbstractWidgetProps, StagePanelLocation, StagePanelSection, UiItemsProvider } from "@itwin/appui-abstract";

import { Input, Button } from "@itwin/itwinui-react";

import { NavigationDataProvider, NavigationNode, NavigationSelectionHandler } from "./TreeHandler";
import { CheckBoxState, useDisposable } from "@itwin/core-react";
import { CssProperties } from "@itwin/appui-layout-react";

import "./NavigationTree.scss";

import { cSelection } from "./Selection";

import categoryTree from "./tree_category.json";
import areaTree from "./tree_area.json";
import systemTree from "./tree_system.json";

import "./Trees.scss";
import { cQuery } from "./Query";

interface NavigationProps {
  root: NavigationNode;
  callback: any;
  keyword: string;
  selection: string[];
}

const TreeWidget: FunctionComponent<NavigationProps> = ({ root, callback, keyword, selection }) => {
  //  const [dataProvider, setTreeDataProvider] = useState(new NavigationDataProvider(root, keyword));

  // useEffect(() => {
  //   setTreeDataProvider(new NavigationDataProvider(root, keyword));
  // }, [keyword]);

  // useEffect(() => {
  //   dataProvider.setSearchString(keyword);
  //   dataProvider.setNavigationRoot(root);
  // }, [dataProvider]);

  // create tree node loader to load nodes.  creates tree model source node loader.
  // Tree hierarchy is passed as prop of the component
  const dataProvider = useMemo(() => new NavigationDataProvider(root, keyword), [root, keyword, selection]);

  dataProvider.setNavigationRoot(root);
  dataProvider.setSearchString(keyword);
  dataProvider.setSelection(selection);
  
  // create model source for tree. Model source contains tree model and provides an
  // API to modify model and listen for changes in tree model.
  // useTreeModelSource() creates new model source when 'dataProvider' object changes
  const modelSource = useTreeModelSource(dataProvider);

  // create tree node loader to load nodes to tree model. It uses 'dataProvider' to get
  // nodes and adds them to tree model using 'modelSource'
  const nodeLoader = useTreeNodeLoader(dataProvider, modelSource);

  // create event handler that handles tree events: expanding/collapsing, selecting/deselecting nodes. Additionally it adds/removes keys to/from
  // Unified Selection when nodes are selected/deselected and updates nodes' selection in tree model to match Unified Selection.
  // useUnifiedSelectionTreeEventHandler() creates new event handler when object passed to it changes. It only accepts
  // node loaders that uses `IPresentationTreeDataProvider`
  const eventHandlerParams = useMemo(() => ({ nodeLoader, modelSource, collapsedChildrenDisposalEnabled: true }), [nodeLoader, modelSource]);

  const eventHandler = useDisposable(useCallback(() => new NavigationSelectionHandler(nodeLoader), [nodeLoader]));

  eventHandler.setSelectionCallback(callback);

  // get list of visible nodes to render in `ControlledTree`. This is a flat list of nodes in tree model.
  // useVisibleTreeNodes() uses 'modelSource' to get flat list of nodes and listens for model changes to
  // re-render component with updated nodes list
  const visibleNodes = useTreeModel(nodeLoader.modelSource);

  return (
    <div className="unified-selection-tree">
      <ControlledTree
        nodeLoader={nodeLoader}
        selectionMode={SelectionMode.Single}
        model={visibleNodes}
        eventsHandler={eventHandler}
        width={300}
        height={400}
      />
    </div>
  );
};

//=======================================================================
//  Navigation Widget
//=======================================================================

const NavWidget: FunctionComponent<{ root: NavigationNode }> = ({ root }) => {
  const [_tag, setTag] = useState("");
  const [_root, setRoot] = useState(root);
  const [_type, setType] = useState("area");
  const [_selection, setSelection] = useState<string[]>([]);
  let keyword: string = "";

  // useEffect(() => {
  //   cSelection.setOnElementSelected("navigation", elementSelected);
  //   return () => {
  //     cSelection.setOnElementSelected("navigation");
  //   };
  // }, []);

  const elementSelected = async (elementTags: string[]) => {
    setSelection(elementTags);
  }

  const setBtnState = (state: boolean): CssProperties => {
    var props: CssProperties;
    const btnActive = "#005596"; // dark dogerblue
    props = state ? { backgroundColor: btnActive, color: "white" } : {};

    return props;
  }

  const onChangeSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
    keyword = event.target.value;
    if (keyword === "") setTag(keyword); // when x in search box is clicked
  }

  const onKeyDnSearch = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setTag(keyword);
    }
  }

  const onButtonSearch = async () => {
    setTag(keyword);
  }

  const onButtonArea = () => {
    setRoot(areaTree);
    setType("area");
  }

  const onButtonCategory = () => {
    setRoot(systemTree);
    setType("system");
  }

  const navCallback = async (label: string) => {
    console.log("NavigationWidget::Area node selected: " + label);
    await cSelection.selectElementByTag(label);
  }

  return (
    <div>
      <div className="search-options">
        <Input
          type="search"
          id="search"
          placeholder="Enter Tag/Id"
          onChange={onChangeSearch}
          onKeyDown={onKeyDnSearch}
          size="small"
        ></Input>
        <Button
          styleType="cta"
          style={{ backgroundColor: "#3b86cc", color: "white" }}
          size="small"
          onClick={onButtonSearch}
        >
          <span className="icon icon-zoom"> </span>
        </Button>
      </div>
      <hr />

      <div className="navigation-2col">
        <Button
          styleType="borderless"
          size="small"
          onClick={onButtonArea}
          style={setBtnState(_type === "area")}
        >
          {"Area"}
        </Button>
        <Button
          styleType="borderless"
          size="small"
          onClick={onButtonCategory}
          style={setBtnState(_type === "system")}
        >
          {"System"}
        </Button>
      </div>

      <div className="navigation-widget">
        <TreeWidget root={_root} callback={navCallback} keyword={_tag} selection = {_selection}/>
      </div>
    </div>
  );
};

//=======================================================================
//  Navigation Widget Provider - Area and Category
//=======================================================================

export class NavigationWidget implements UiItemsProvider {
  public readonly id: string = "NavigationWidget";

  public provideWidgets(stageId: string, stageUsage: string, location: StagePanelLocation, section?: StagePanelSection): ReadonlyArray<AbstractWidgetProps> {
    const widgets: AbstractWidgetProps[] = [];
    if (location === StagePanelLocation.Right && section === StagePanelSection.Start) {
      const areaWidget: AbstractWidgetProps = {
        id: "AreaTreeWidget",
        //        label: "Area",
        label: "Navigation",
        //!!OA        defaultState: WidgetState.Floating,
        getWidgetContent() {
          return <NavWidget root={areaTree} />;
        },
      };
      widgets.push(areaWidget);
    }

    //     if (location === StagePanelLocation.Right && section === StagePanelSection.Start) {
    //       const catWidget: AbstractWidgetProps = {
    //         id: "CategoryTreeWidget",
    //         label: "Category",
    // //!!OA        defaultState: WidgetState.Floating,
    //         getWidgetContent() {
    //           return <NavWidget root={categoryTree}/>
    //         }
    //       }
    //       widgets.push(catWidget);
    //     }

    return widgets;
  }
}
//=======================================================================
